import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { DateTimePicker, Drawer, Field, PinIcon, relativeTime, Segments, Select, toISODate, TrashIcon, useConfirm, useToast } from "@signet/ui";
import { useCreatorPhotos, useMe } from "@signet/web-kit";
import { BASE, deleteTodo, PRIORITIES, PRIORITY_LABEL, todoKeys, type Priority, type Todo } from "./api.ts";
import { NotesEditor, NotesView } from "./notes.tsx";
import { Subitems } from "./subitems.tsx";
import { useTodoChoices, useTodoUpdate } from "./row.tsx";
import { useTick } from "./telegram.tsx";

/* ──────────────────────────────────────────────────────────────────────────
   One to-do, opened. Every field saves on its own as it changes: the name
   and notes when you leave them, everything else at once. Nothing but the
   name is ever needed.
   ────────────────────────────────────────────────────────────────────────── */

const messageOf = (error: unknown) => (error instanceof Error ? error.message : "Something went wrong.");

type PriorityChoice = Priority | "none";

export function TodoDrawer({ todo, onClose }: { todo: Todo | null; onClose: () => void }) {
  const { can } = useMe();
  const editable = can("todo.item:edit");
  const update = useTodoUpdate();
  const client = useQueryClient();
  const toast = useToast();
  const { confirm, element } = useConfirm();
  const photos = useCreatorPhotos(BASE);
  const choices = useTodoChoices(Boolean(todo));
  const [name, setName] = useState("");

  useEffect(() => {
    setName(todo?.name ?? "");
  }, [todo?.id]);

  const remove = useMutation({
    mutationFn: (id: string) => deleteTodo(id),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: todoKeys.all });
      onClose();
    },
    onError: (error) => toast.error("Couldn't delete it", messageOf(error)),
  });

  if (!todo) return null;
  const save = (patch: Parameters<typeof update.mutate>[0]["patch"]) => update.mutate({ id: todo.id, patch });
  const saveName = () => {
    if (name.trim() && name.trim() !== todo.name) save({ name: name.trim() });
    else setName(todo.name);
  };
  // Only creators on the roster can be picked; one who has left stays shown on a to-do that already names them.
  const creatorOptions = [...new Set([...choices.creators, ...(todo.creator ? [todo.creator] : [])])]
    .sort()
    .map((c) => ({ value: c, label: c, image: photos(c) }));

  return (
    <Drawer
      open
      narrow
      onClose={onClose}
      title={todo.done ? "Done" : "To-do"}
      subtitle={`Added by ${todo.createdBy ?? "someone"} ${relativeTime(todo.createdAt)}${todo.updatedBy && todo.updatedAt !== todo.createdAt ? `, last changed by ${todo.updatedBy} ${relativeTime(todo.updatedAt)}` : ""}`}
    >
      {element}
      <div className="td-drawer">
        <textarea
          className="td-name-input"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onBlur={saveName}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              (e.target as HTMLTextAreaElement).blur();
            }
          }}
          rows={2}
          maxLength={300}
          readOnly={!editable}
          aria-label="Name"
        />

        <div className="td-drawer-actions">
          <TickButton todo={todo} editable={editable} />
          <button type="button" className={`secondary-button td-pin-button${todo.pinned ? " on" : ""}`} disabled={!editable} aria-pressed={todo.pinned} onClick={() => save({ pinned: !todo.pinned })}>
            <PinIcon size={15} /> {todo.pinned ? "Pinned" : "Pin to the top"}
          </button>
        </div>
        {todo.done && todo.doneBy ? <p className="td-faint">Ticked off by {todo.doneBy} {todo.doneAt ? relativeTime(todo.doneAt) : ""}</p> : null}

        <Field label="Priority">
          <Segments<PriorityChoice>
            label="Priority"
            value={todo.priority ?? "none"}
            options={[{ value: "none", label: "None" }, ...PRIORITIES.map((p) => ({ value: p, label: PRIORITY_LABEL[p] }))]}
            onChange={(value) => editable && save({ priority: value === "none" ? null : value })}
          />
        </Field>

        <Field label="Due">
          <DateTimePicker
            label="Due date"
            value={todo.dueOn}
            withTime={false}
            placeholder="No due date"
            onChange={(iso) => editable && save({ dueOn: iso ? toISODate(new Date(iso)) : null })}
          />
        </Field>

        <Field label="Who's doing it" hint="Only people with access to To-do can be picked.">
          <Select
            label="Who's doing it"
            value={todo.assignee?.id ?? null}
            options={choices.people.map((p) => ({ value: p.id, label: p.name }))}
            onChange={(id) => editable && save({ assignee: id })}
            placeholder="Nobody yet"
            clearable
            disabled={!editable}
          />
        </Field>

        <Field label="Creator">
          <Select
            label="Creator"
            value={todo.creator}
            options={creatorOptions}
            onChange={(creator) => editable && save({ creator })}
            placeholder="No creator"
            clearable
            searchable
            disabled={!editable}
          />
        </Field>

        <Subitems todo={todo} editable={editable} />

        <div className="td-notes">
          <div className="td-notes-head">
            <span>Notes</span>
          </div>
          {editable ? <NotesEditor key={todo.id} notes={todo.notes} editable onSave={(notes) => save({ notes })} /> : todo.notes ? <NotesView notes={todo.notes} /> : <p className="td-faint">No notes.</p>}
        </div>

        {can("todo.item:delete") ? (
          <button
            type="button"
            className="secondary-button compact td-danger"
            onClick={async () => {
              if (await confirm({ title: `Delete "${todo.name}"?`, body: "It's gone for everyone. To keep it, tick it off instead.", confirmLabel: "Delete", danger: true, enterConfirms: true })) remove.mutate(todo.id);
            }}
          >
            <TrashIcon size={13} /> Delete to-do
          </button>
        ) : null}
      </div>
    </Drawer>
  );
}

/** Its own component so the tick (and Jay's what's-next question) can use hooks after the drawer's early return. */
function TickButton({ todo, editable }: { todo: Todo; editable: boolean }) {
  const ticking = useTick(todo);
  return (
    <>
      {ticking.picker}
      <button type="button" className={todo.done ? "secondary-button" : "button brass"} disabled={!editable || ticking.pending} onClick={ticking.tick}>
        {todo.done ? "Mark not done" : "Tick it off"}
      </button>
    </>
  );
}
